<script>
import ProspectManufacturerOptionConstant from '@/constants/repairModule/ProspectManufacturerOptionConstant'
import { displayToast, handleResponseError } from '@/components/utils/globalFunction'
import { auth as $store } from '@/store/AuthModule'
import RepairFileProspectService from '@/services/repairModule/repairFile/RepairFileProspectService'
import { RepairFile } from '@/models/RepairFile'
import ConfirmProspectManufacturerResponseModal from '@/components/modal/repair-file/step-prospect-request/ConfirmProspectManufacturerResponseModal.vue'
import Validator from '@/components/utils/validator'
import RepairFileStatusDetailConstant from '@/constants/repairFileStatusDetailConstant'

export default {
  name: 'SubStepProspectResponse',
  components: { ConfirmProspectManufacturerResponseModal },
  computed: {
    ProspectManufacturerOptionConstant() {
      return ProspectManufacturerOptionConstant
    }
  },
  props: {
    currentRepairFile: RepairFile
  },
  data() {
    return {
      loading: false,
      formValid: false,

      prospectSupportNumber: null,
      prospectComment: null,
      file: null,
      selectedManufacturerChoice: null,

      requiredRules: Validator.requiredRules,
      max12CharRules: Validator.max12CharRules,

      error: null,

      currentStatusDetail: RepairFileStatusDetailConstant.PROSPECT_REQUEST_SENT
    }
  },
  created() {
    if (this.currentRepairFile !== null) {
      this.prospectSupportNumber = this.currentRepairFile.prospect_manufacturer_support_number
      this.prospectComment = this.currentRepairFile.prospect_manufacturer_comment
      this.selectedManufacturerChoice = this.currentRepairFile.prospect_manufacturer_choice
      this.currentStatusDetail = RepairFileStatusDetailConstant.PROSPECT_MANUFACTURER_RESPONSE
    }
  },
  methods: {
    async validateProspectManufacturerResponse(isPartial = false) {
      this.loading = true
      const repairFileId = this.currentRepairFile.technical_id
      try {
        const data = {
          status_detail: this.currentStatusDetail,
          repair_file_id: repairFileId,
          requestor_collaborator_id: $store.state.currentCollaborator.technical_id,
          prospect_support_number: this.prospectSupportNumber,
          prospect_manufacturer_comment: this.prospectComment,
          prospect_manufacturer_choice: this.selectedManufacturerChoice,
          is_complete_update: !isPartial
        }
        const formData = new FormData()
        formData.append('data', JSON.stringify(data))
        formData.append('file', this.file)

        const response = await RepairFileProspectService.update(repairFileId, formData)

        displayToast(
          this.$t('STEP_PROSPECT.SUB_STEP_PROSPECT_RESPONSE.MESSAGE.VALIDATE_SUCCESS'),
          'success'
        )
        if (isPartial === false) {
          if (response.continueProcess) {
            this.$emit('go-to-next-step')
          } else {
            this.$router.push(
              '/repair-file-detail/' + response.repairFileBoomerangId + '?skip=true'
            )
          }
        } else {
          this.$router.push({ name: 'Dashboard', query: { skip: 'true' } })
        }
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async openConfirmProspectManufacturerResponseModal() {
      const response = await this.$refs.confirmProspectManufacturerResponseRef.show()

      if (response.confirmed) {
        await this.validateProspectManufacturerResponse()
      }
    },
    async updatePartialData() {
      await this.validateProspectManufacturerResponse(true)
    }
  }
}
</script>

<template>
  <v-container class="form-container">
    <v-form v-model="formValid">
      <!--Support Number-->
      <v-row>
        <v-col col="6">
          <p class="mb-4" :data-custom="true">
            {{ $t('PUBLIC_PROSPECT.SUPPORT_NUMBER_LABEL') }}
          </p>
          <v-text-field
            v-model="prospectSupportNumber"
            :error-messages="error?.prospectSupportNumber"
            :hide-details="!error?.prospectSupportNumber"
            :rules="[...requiredRules, ...max12CharRules]"
            :counter="12"
            :maxlength="12"
          />
        </v-col>
      </v-row>

      <!--Comment-->
      <v-row>
        <v-col col="6">
          <p class="mb-4">
            {{ $t('PUBLIC_PROSPECT.MANUFACTURER_COMMENT_LABEL') }}
          </p>
          <v-textarea
            v-model.trim="prospectComment"
            :placeholder="$t('PUBLIC_PROSPECT.MANUFACTURER_COMMENT_LABEL')"
            auto-grow
            required
            rows="1"
            :error-messages="error?.prospectComment"
            :hide-details="!error?.prospectComment"
            counter
          />
        </v-col>
      </v-row>

      <!--Attachment-->
      <v-row>
        <v-col col="6">
          <!--Add attachment-->
          <p class="mb-4">
            {{ $t('PUBLIC_PROSPECT.MANUFACTURER_ATTACHMENT_LABEL') }}
          </p>
          <v-file-input
            class="logo-selector"
            chips
            counter
            show-size
            accept=".pdf, .jpg, .png"
            v-model="file"
          >
            <template v-slot:append-inner>
              <img
                alt="download.svg"
                class="download-svg mr-4"
                src="@/assets/icons/file/download.svg"
              />
            </template>
          </v-file-input>
        </v-col>
      </v-row>

      <!-- Manufacturer Choice -->
      <v-row>
        <v-col cols="6">
          <h4>{{ $t('STEP_PROSPECT.SUB_STEP_PROSPECT_RESPONSE.MANUFACTURER_SUPPORT_LABEL') }}</h4>

          <v-radio-group v-model="selectedManufacturerChoice" row>
            <v-radio
              :label="
                $t(
                  'STEP_PROSPECT.SUB_STEP_PROSPECT_RESPONSE.MANUFACTURER_OPTION.PRODUCT_REPAIR_WITHOUT_GUARANTEE'
                )
              "
              :value="ProspectManufacturerOptionConstant.PRODUCT_REPAIR_WITHOUT_GUARANTEE"
              color="#559a1b"
              outlined
            />
            <v-radio
              :label="
                $t('STEP_PROSPECT.SUB_STEP_PROSPECT_RESPONSE.MANUFACTURER_OPTION.CREDIT_OR_REFUND')
              "
              :value="ProspectManufacturerOptionConstant.CREDIT_OR_REFUND"
              color="#559a1b"
              outlined
            />
            <v-radio
              :label="
                $t(
                  'STEP_PROSPECT.SUB_STEP_PROSPECT_RESPONSE.MANUFACTURER_OPTION.PRODUCT_REPLACEMENT'
                )
              "
              :value="ProspectManufacturerOptionConstant.PRODUCT_REPLACEMENT"
              color="#559a1b"
              outlined
            />
            <v-radio
              :label="
                $t('STEP_PROSPECT.SUB_STEP_PROSPECT_RESPONSE.MANUFACTURER_OPTION.REJECT_PROSPECT')
              "
              :value="ProspectManufacturerOptionConstant.REJECT_PROSPECT"
              color="#559a1b"
              outlined
            />
            <v-radio
              :label="$t('STEP_PROSPECT.SUB_STEP_PROSPECT_RESPONSE.MANUFACTURER_OPTION.OTHER')"
              :value="ProspectManufacturerOptionConstant.OTHER"
              color="#559a1b"
              outlined
            />
          </v-radio-group>
        </v-col>
      </v-row>

      <div class="btn-container">
        <v-btn
          class="boom-button"
          :disabled="!formValid || !selectedManufacturerChoice || loading"
          :loading="loading"
          variant="outlined"
          type="button"
          @click="openConfirmProspectManufacturerResponseModal"
        >
          {{ $t('COMMON.BUTTON.CONTINUE') }}
        </v-btn>
      </div>
    </v-form>
  </v-container>
  <ConfirmProspectManufacturerResponseModal ref="confirmProspectManufacturerResponseRef" />
</template>

<style scoped>
.form-container {
  font-family: 'Roboto', serif;
}

:deep(.logo-selector .v-field.v-field--appended) {
  justify-content: center !important;
  align-content: center;
}

.download-svg {
  height: 20px;
  filter: invert(98%) sepia(0%) saturate(1853%) hue-rotate(181deg) brightness(96%) contrast(80%);
}
</style>
